import { motion, useReducedMotion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Eyebrow } from '../components/Eyebrow';
import { Button } from '../components/Button';
import { StatusChip } from '../components/StatusChip';
import { GradientTileSequence } from '../components/GradientTileSequence';
import { Reveal } from '../components/motion/Reveal';
import { AnimatedBuildingScene } from '../components/home/AnimatedBuildingScene';

const COVERAGES: { label: string; eyebrow: string; note: string }[] = [
  { label: 'Builders Risk', eyebrow: 'COVERAGE_01', note: 'New construction, ground-up and rehab.' },
  { label: 'Property', eyebrow: 'COVERAGE_02', note: 'Permanent coverage for occupied buildings.' },
  { label: 'General Liability', eyebrow: 'COVERAGE_03', note: 'Third-party bodily injury + property.' },
  { label: 'Excess', eyebrow: 'COVERAGE_04', note: '$5M umbrella above GL.' },
  { label: 'Pollution', eyebrow: 'COVERAGE_05', note: 'Environmental liability.' },
  { label: 'Owners Interest', eyebrow: 'COVERAGE_06', note: 'Developer-side OI packages.' },
];

const STEPS: { label: string; eyebrow: string; note: string }[] = [
  { eyebrow: '01', label: 'Fill in your project', note: 'Four steps: project, budget, term, deductibles.' },
  { eyebrow: '02', label: 'See three scenarios', note: 'Best case, average, and conservative ranges.' },
  { eyebrow: '03', label: 'Share your email', note: 'One-field gate. Never shared.' },
  { eyebrow: '04', label: 'A broker follows up', note: 'Derek Krenke with the formal indication.' },
];

export function Home() {
  const reduced = useReducedMotion() ?? false;

  const fadeUp = (delay: number) =>
    reduced
      ? { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 } }
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] as const },
        };

  return (
    <>
      <Navbar />

      <main>
        {/* HERO — compact */}
        <section className="container-site pt-6 pb-10 md:pt-8 md:pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-7 flex flex-col gap-4">
              <motion.div {...fadeUp(0)}>
                <Eyebrow>PROPERTY_INSURANCE · UPPER_MIDWEST</Eyebrow>
              </motion.div>

              <motion.h1
                {...fadeUp(0.08)}
                className="font-display font-medium tracking-[-0.02em] leading-[1.04] text-[clamp(30px,4.4vw,50px)]"
              >
                Builders Risk and Property Insurance{' '}
                <span className="text-ink-muted">done right.</span>
              </motion.h1>

              <motion.p
                {...fadeUp(0.16)}
                className="max-w-xl text-[0.9375rem] leading-relaxed text-ink-muted"
              >
                We&apos;re the fastest-growing property-insurance brokerage serving multifamily
                operators across the Upper Midwest. Get a real indication in minutes. Then a real
                broker follows up.
              </motion.p>

              <motion.div {...fadeUp(0.24)} className="flex flex-wrap gap-3 pt-1">
                <Button as="link" to="/indication" variant="primary" size="sm">
                  Builders Risk Indication
                </Button>
                <Button as="link" to="/property" variant="secondary" size="sm">
                  Property Insurance Quote
                </Button>
              </motion.div>

              <motion.div
                {...fadeUp(0.32)}
                className="flex flex-wrap gap-x-4 gap-y-1.5 pt-3 items-center"
              >
                <StatusChip label="BUILDERS_RISK · SINCE_2015" />
                <StatusChip label="MULTIFAMILY_SPECIALIZED" />
                <StatusChip label="10K+_UNITS_INSURED" />
              </motion.div>
            </div>

            <div className="lg:col-span-5 lg:mt-32">
              <motion.div
                initial={reduced ? { opacity: 1 } : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <AnimatedBuildingScene />
              </motion.div>
            </div>
          </div>
        </section>

        {/* QUIET CTA PANEL — moved above What We Cover */}
        <Reveal>
          <section className="container-site py-8 md:py-10 border-t border-hairline">
            <div className="relative bg-panel border border-hairline rounded-md p-6 md:p-8 overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-primary"
              />
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex flex-col gap-2 max-w-xl">
                  <Eyebrow>READY_WHEN_YOU_ARE</Eyebrow>
                  <h3 className="font-display font-medium tracking-[-0.015em] leading-[1.2] text-[clamp(20px,2.6vw,28px)]">
                    Ready when you are.
                  </h3>
                  <p className="text-ink-muted text-[0.9375rem]">
                    Start with a builders risk indication. A real broker reviews and follows up
                    with the formal numbers.
                  </p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button as="link" to="/indication" variant="primary" size="sm">
                    Builders Risk Indication
                  </Button>
                  <Button as="link" to="/property" variant="secondary" size="sm">
                    Property Insurance Quote
                  </Button>
                  <StatusChip label="AVG_RESPONSE · 2_BUSINESS_DAYS" />
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        {/* COVERAGE STRIP */}
        <Reveal>
          <section className="container-site py-10 md:py-12 border-t border-hairline">
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <Eyebrow>WHAT_WE_COVER</Eyebrow>
                <h2 className="mt-2 font-display font-medium tracking-[-0.015em] leading-[1.15] text-[clamp(22px,3vw,32px)] max-w-3xl">
                  Every line a multifamily operator needs, under one roof.
                </h2>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {COVERAGES.map((c) => (
                <div
                  key={c.label}
                  className="bg-panel border border-hairline rounded-md p-3 flex flex-col gap-1.5 min-h-[110px] hover:border-ink/40 transition-colors"
                >
                  <Eyebrow>{c.eyebrow}</Eyebrow>
                  <p className="font-display font-medium text-[0.9375rem] leading-tight">
                    {c.label}
                  </p>
                  <p className="text-[11px] text-ink-subtle leading-snug mt-auto">{c.note}</p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        {/* HOW IT WORKS */}
        <Reveal>
          <section id="how-it-works" className="container-site py-10 md:py-14 border-t border-hairline">
            <Eyebrow>HOW_IT_WORKS</Eyebrow>
            <h2 className="mt-2 font-display font-medium tracking-[-0.015em] leading-[1.15] text-[clamp(22px,3vw,32px)] max-w-2xl">
              Four steps. A real indication.
            </h2>

            <div className="mt-6 max-w-5xl">
              <GradientTileSequence count={STEPS.length} tileHeight={8} />
              <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                {STEPS.map((s) => (
                  <div key={s.eyebrow} className="flex flex-col gap-1">
                    <span className="font-display font-light tabular-nums text-[32px] leading-none text-ink">
                      {s.eyebrow}
                    </span>
                    <p className="font-display font-medium text-[0.9375rem] leading-tight mt-1">
                      {s.label}
                    </p>
                    <p className="text-[13px] text-ink-muted leading-snug">{s.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>
      </main>

      <Footer />
    </>
  );
}
